// Cadastro da transportadora no MeuFrete (PO1-2308): Dados principais
// (matriz = o login), Filiais (os outros estabelecimentos da raiz, editar sem
// remover) e Contatos (por embarcador escolhido no topo; criar, copiar de outro
// embarcador e editar com propagação por e-mail).
// PO1-3359 (Rede Everlog): com vínculo com a Rede (GET /meufrete/rede/status →
// temRede), entram as abas Cobertura (PO1-3648), Tabelas de referência e API de
// cotação (PO1-3650). Sem vínculo, a tela é a de sempre.
// Nos onChange, o valor do campo é lido ANTES do setState: no React 16 o evento
// sintético é devolvido ao pool antes do updater funcional rodar, e ler e.target lá
// dentro derruba a tela (null) quando se digita rápido.
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Nav, NavItem, NavLink, Row, Spinner, TabContent, Table, TabPane } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import CadastroEstabelecimentoForm, { resumoReceita } from "components/CadastroEstabelecimentoForm";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import {
  getCadastro,
  atualizarEstabelecimento,
  atualizarPelaReceita,
  listarTiposContato,
  listarContatos,
  sugestoesContatos,
  criarContato,
  atualizarContato,
  redeStatus,
  mensagemErro,
} from "services/meuFreteApi";
import { formatDocument } from "utils/formatDoc";
import { enviarLogoMeuFrete, removerLogoMeuFrete, mensagemErroLogo } from "services/transportadoraLogoApi";
import AbaCobertura from "./rede/AbaCobertura";
import AbaTabelasReferencia from "./rede/AbaTabelasReferencia";
import AbaApiCotacao from "./rede/AbaApiCotacao";

const TABS = [
  { id: "dados", label: "Dados principais" },
  { id: "filiais", label: "Filiais" },
  { id: "contatos", label: "Contatos" },
];
// Só com vínculo com a Rede Everlog (ids também são os da Central de Treinamento).
const TABS_REDE = [
  { id: "cobertura", label: "Cobertura" },
  { id: "tabelas-referencia", label: "Tabelas de referência" },
  { id: "api", label: "API de cotação" },
];
const IDS_TABS = TABS.map((t) => t.id);
const IDS_TABS_REDE = TABS_REDE.map((t) => t.id);

// A aba aberta fica no endereço (?aba=): o ícone de ajuda da Central abre o tutorial
// no ponto da aba e o aviso de boas-vindas da Rede leva direto para a Cobertura.
function abaDoEndereco(search) {
  const aba = new URLSearchParams(search || "").get("aba");
  return IDS_TABS.indexOf(aba) >= 0 || IDS_TABS_REDE.indexOf(aba) >= 0 ? aba : null;
}
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };
const CONTATO_VAZIO = { nome: "", email: "", idTipoContato: "", dddTelefone: "", telefone: "", dddCelular: "", celular: "" };

function telefone(ddd, numero) {
  if (!numero) return "";
  return ddd ? `(${ddd}) ${numero}` : numero;
}

export default function CadastroTransportadora() {
  const { addToast } = useToast();
  const { empresa, isAllEmpresas } = useCompanies();
  const embarcadorId = empresa && !isAllEmpresas ? empresa.value : null;

  const location = useLocation();
  const history = useHistory();
  const [aba, setAba] = useState(() => abaDoEndereco(location.search) || "dados");
  const [cadastro, setCadastro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [filialEmEdicao, setFilialEmEdicao] = useState(null);

  const [tipos, setTipos] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [modalContato, setModalContato] = useState(null); // { modo: 'novo'|'editar', contato }
  const [formContato, setFormContato] = useState(CONTATO_VAZIO);
  // PO1-3359: vínculo com a Rede Everlog libera as 3 abas. Falha = sem abas.
  const [temRede, setTemRede] = useState(false);
  const [redeConferida, setRedeConferida] = useState(false);

  useEffect(() => {
    let ativo = true;
    redeStatus()
      .then((r) => {
        if (ativo) setTemRede(Boolean(r && r.temRede));
      })
      .catch(() => {
        if (ativo) setTemRede(false);
      })
      .finally(() => {
        if (ativo) setRedeConferida(true);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Endereço mudou por fora (ícone de ajuda, link do aviso): segue a aba dele.
  useEffect(() => {
    const daUrl = abaDoEndereco(location.search);
    if (daUrl) setAba(daUrl);
  }, [location.search]);

  // Aba da Rede no endereço sem vínculo com a Rede: volta para os dados principais.
  useEffect(() => {
    if (redeConferida && !temRede && IDS_TABS_REDE.indexOf(aba) >= 0) setAba("dados");
  }, [redeConferida, temRede, aba]);

  const trocarAba = (id) => {
    setAba(id);
    history.replace({ pathname: location.pathname, search: `?aba=${id}` });
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      setCadastro(await getCadastro());
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível carregar o cadastro."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const carregarContatos = useCallback(async () => {
    if (!embarcadorId) {
      setContatos([]);
      setSugestoes([]);
      return;
    }
    setLoadingContatos(true);
    try {
      const [lista, sug, tp] = await Promise.all([
        listarContatos(embarcadorId),
        sugestoesContatos(embarcadorId),
        tipos.length ? Promise.resolve(tipos) : listarTiposContato(),
      ]);
      setContatos(lista || []);
      setSugestoes(sug || []);
      if (!tipos.length) setTipos(tp || []);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Erro ao carregar os contatos", description: mensagemErro(err) });
    } finally {
      setLoadingContatos(false);
    }
  }, [embarcadorId, tipos, addToast]);

  useEffect(() => {
    if (aba === "contatos") carregarContatos();
  }, [aba, carregarContatos]);

  const salvarEstabelecimento = async (id, dados) => {
    setSalvando(true);
    try {
      const out = await atualizarEstabelecimento(id, dados);
      addToast({ place: "tr", type: "success", title: "Cadastro atualizado", description: out.alterados && out.alterados.length ? `Campos alterados: ${out.alterados.join(", ")}.` : "Nada mudou." });
      setFilialEmEdicao(null);
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar", description: mensagemErro(err) });
    } finally {
      setSalvando(false);
    }
  };

  // Logo (PO1-2722): ação própria, fora do Salvar. Depois do upload o cadastro
  // é recarregado e o estabelecimento volta com o logoUrl novo.
  const [logoBusy, setLogoBusy] = useState(false);
  const enviarLogo = async (id, blob, nome) => {
    setLogoBusy(true);
    try {
      await enviarLogoMeuFrete(id, blob, nome);
      addToast({ place: "tr", type: "success", title: "Logo atualizado", description: "Já aparece para os embarcadores que veem esta transportadora." });
      const c = await getCadastro();
      setCadastro(c);
      if (filialEmEdicao) setFilialEmEdicao((c.filiais || []).find((f) => f.id === filialEmEdicao.id) || null);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível enviar o logo", description: mensagemErroLogo(err) });
    } finally {
      setLogoBusy(false);
    }
  };
  const removerLogo = async (id) => {
    setLogoBusy(true);
    try {
      await removerLogoMeuFrete(id);
      addToast({ place: "tr", type: "success", title: "Logo removido", description: "Volta a aparecer o monograma com as iniciais." });
      const c = await getCadastro();
      setCadastro(c);
      if (filialEmEdicao) setFilialEmEdicao((c.filiais || []).find((f) => f.id === filialEmEdicao.id) || null);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover o logo", description: mensagemErroLogo(err) });
    } finally {
      setLogoBusy(false);
    }
  };

  const receita = async (id) => {
    try {
      const out = await atualizarPelaReceita(id);
      addToast({
        place: "tr",
        type: "success",
        title: "Receita consultada",
        description: resumoReceita(out),
      });
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível consultar a Receita", description: mensagemErro(err) });
    }
  };

  const abrirNovoContato = (base) => {
    setFormContato({
      ...CONTATO_VAZIO,
      ...(base ? { nome: base.nome || "", email: base.email || "", idTipoContato: base.idTipoContato || "", dddTelefone: base.dddTelefone || "", telefone: base.telefone || "", dddCelular: base.dddCelular || "", celular: base.celular || "" } : {}),
    });
    setModalContato({ modo: "novo", contato: null, copiaDe: base ? base.embarcadorOrigem : null });
  };

  const abrirEditarContato = (c) => {
    setFormContato({ nome: c.nome || "", email: c.email || "", idTipoContato: c.idTipoContato || "", dddTelefone: c.dddTelefone || "", telefone: c.telefone || "", dddCelular: c.dddCelular || "", celular: c.celular || "" });
    setModalContato({ modo: "editar", contato: c });
  };

  const salvarContato = async () => {
    setSalvando(true);
    try {
      if (modalContato.modo === "novo") {
        await criarContato({ embarcadorId, ...formContato });
        addToast({ place: "tr", type: "success", title: "Contato criado" });
      } else {
        const { atualizados } = await atualizarContato(modalContato.contato.id, formContato);
        addToast({ place: "tr", type: "success", title: "Contato atualizado", description: `${atualizados} registro(s) atualizado(s), em todos os embarcadores com este e-mail.` });
      }
      setModalContato(null);
      await carregarContatos();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar o contato", description: mensagemErro(err) });
    } finally {
      setSalvando(false);
    }
  };

  const matriz = cadastro && cadastro.matriz;
  const filiais = (cadastro && cadastro.filiais) || [];
  const navStyle = { cursor: "pointer", fontSize: "0.85rem", padding: "0.45rem 0.9rem", color: AUTOPILOT_COLORS.muted };
  const navActiveStyle = { ...navStyle, color: AUTOPILOT_COLORS.text, borderBottom: `2px solid ${AUTOPILOT_COLORS.accent}`, fontWeight: 600 };

  return (
    <div className="content">
      <PageHero
        eyebrow="MeuFrete"
        title="Cadastro"
        description={`Dados principais e filiais valem para todos os embarcadores; os contatos são por embarcador. CNPJ não muda; a razão social só pela Receita Federal.${temRede ? " Cobertura, tabelas de referência e API de cotação são da Rede Everlog." : ""}`}
        actions={<PageHeroRefreshButton onClick={carregar} loading={loading} />}
      />

      <ShellCard>
        <Nav style={{ marginBottom: "1rem", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
          {TABS.concat(temRede ? TABS_REDE : []).map((t) => (
            <NavItem key={t.id}>
              <NavLink style={aba === t.id ? navActiveStyle : navStyle} onClick={() => trocarAba(t.id)}>
                {t.label}
                {t.id === "filiais" ? ` (${filiais.length})` : ""}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {loading && !cadastro ? (
          <div style={stateBoxStyle}><Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /><span>Carregando…</span></div>
        ) : erro ? (
          <div style={stateBoxStyle} role="alert">{erro}</div>
        ) : (
          <TabContent activeTab={aba}>
            <TabPane tabId="dados">
              {matriz ? (
                <SectionCard eyebrow="Matriz" title={matriz.fantasia || matriz.razaoSocial} description={`CNPJ ${formatDocument(matriz.cnpjCpf)}`}>
                  <CadastroEstabelecimentoForm
                    estabelecimento={matriz}
                    salvando={salvando}
                    onSalvar={(dados) => salvarEstabelecimento(matriz.id, dados)}
                    onReceita={() => receita(matriz.id)}
                    onEnviarLogo={(blob, nome) => enviarLogo(matriz.id, blob, nome)}
                    onRemoverLogo={() => removerLogo(matriz.id)}
                    logoBusy={logoBusy}
                  />
                </SectionCard>
              ) : (
                <div style={stateBoxStyle}>Cadastro da matriz não encontrado.</div>
              )}
            </TabPane>

            <TabPane tabId="filiais">
              <p style={mutedStyle}>Outros estabelecimentos da mesma raiz de CNPJ. Você edita os dados; a estrutura de matriz e filiais (e a remoção) fica com a Everlog.</p>
              {!filiais.length ? (
                <div style={stateBoxStyle}>Nenhuma filial cadastrada.</div>
              ) : (
                <Table size="sm" responsive hover className="hub-thead-accent">
                  <thead className="text-primary">
                    <tr><th>Filial</th><th>CNPJ</th><th>Cidade / UF</th><th>Telefone</th><th>Situação</th><th /></tr>
                  </thead>
                  <tbody>
                    {filiais.map((f) => (
                      <tr key={f.id}>
                        <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{f.fantasia || f.razaoSocial}</td>
                        <td>{formatDocument(f.cnpjCpf)}</td>
                        <td>{f.cidade ? `${f.cidade} / ${f.uf}` : "—"}</td>
                        <td>{telefone(f.dddTelefone, f.telefone) || "—"}</td>
                        <td>{f.status ? "Ativa" : "Inativa"}</td>
                        <td className="text-right"><Button size="sm" color="info" outline onClick={() => setFilialEmEdicao(f)}>Editar</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </TabPane>

            <TabPane tabId="contatos">
              {!embarcadorId ? (
                <div style={stateBoxStyle}>Os contatos são por embarcador: escolha um embarcador específico no topo da página.</div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.75rem", flexWrap: "wrap" }}>
                    <span style={mutedStyle}>Contatos de <strong style={{ color: AUTOPILOT_COLORS.title }}>{empresa.label}</strong>. Editar um contato atualiza os dados da pessoa em todos os embarcadores que usam o mesmo e-mail.</span>
                    <Button size="sm" color="info" onClick={() => abrirNovoContato(null)}>+ Adicionar</Button>
                  </div>
                  {loadingContatos ? (
                    <div style={stateBoxStyle}><Spinner size="sm" /><span>Carregando…</span></div>
                  ) : (
                    <>
                      {sugestoes.length > 0 && (
                        <SectionCard variant="field" eyebrow="Copiar de outro embarcador" style={{ marginBottom: "0.75rem" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                            {sugestoes.map((s) => (
                              <Button key={s.id} size="sm" color="secondary" outline onClick={() => abrirNovoContato(s)} title={`Cadastrado em ${s.embarcadorOrigem}`}>
                                {s.nome} · {s.email}
                              </Button>
                            ))}
                          </div>
                        </SectionCard>
                      )}
                      {!contatos.length ? (
                        <div style={stateBoxStyle}>Nenhum contato para este embarcador.</div>
                      ) : (
                        <Table size="sm" responsive hover className="hub-thead-accent">
                          <thead className="text-primary">
                            <tr><th>Tipo</th><th>Nome</th><th>E-mail</th><th>Telefone</th><th>Celular</th><th /></tr>
                          </thead>
                          <tbody>
                            {contatos.map((c) => (
                              <tr key={c.id}>
                                <td>{c.tipoContato || c.idTipoContato}</td>
                                <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{c.nome}</td>
                                <td>
                                  {c.email || "—"}
                                  {c.qtdEmbarcadoresMesmoEmail > 1 && <div style={mutedStyle}>em {c.qtdEmbarcadoresMesmoEmail} embarcadores</div>}
                                </td>
                                <td>{telefone(c.dddTelefone, c.telefone) || "—"}</td>
                                <td>{telefone(c.dddCelular, c.celular) || "—"}</td>
                                <td className="text-right"><Button size="sm" color="info" outline onClick={() => abrirEditarContato(c)}>Editar</Button></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </>
              )}
            </TabPane>

            {/* PO1-3359: abas da Rede Everlog, montadas só quando ativas (cada
                uma carrega os próprios dados ao abrir). */}
            {temRede ? (
              <>
                <TabPane tabId="cobertura">{aba === "cobertura" ? <AbaCobertura /> : null}</TabPane>
                <TabPane tabId="tabelas-referencia">{aba === "tabelas-referencia" ? <AbaTabelasReferencia /> : null}</TabPane>
                <TabPane tabId="api">{aba === "api" ? <AbaApiCotacao /> : null}</TabPane>
              </>
            ) : null}
          </TabContent>
        )}
      </ShellCard>

      <Modal isOpen={!!filialEmEdicao} toggle={salvando ? undefined : () => setFilialEmEdicao(null)} modalClassName="modal-align-top" fade={false} size="lg">
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.75rem" }}>Editar filial</div>
          {filialEmEdicao && (
            <CadastroEstabelecimentoForm
              estabelecimento={filialEmEdicao}
              salvando={salvando}
              compact
              onSalvar={(dados) => salvarEstabelecimento(filialEmEdicao.id, dados)}
              onReceita={() => receita(filialEmEdicao.id)}
              onCancelar={() => setFilialEmEdicao(null)}
              onEnviarLogo={(blob, nome) => enviarLogo(filialEmEdicao.id, blob, nome)}
              onRemoverLogo={() => removerLogo(filialEmEdicao.id)}
              logoBusy={logoBusy}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!modalContato} toggle={salvando ? undefined : () => setModalContato(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.35rem" }}>
            {modalContato && modalContato.modo === "novo" ? "Novo contato" : "Editar contato"}
          </div>
          {modalContato && modalContato.copiaDe && <div style={{ ...mutedStyle, marginBottom: "0.75rem" }}>Copiado de {modalContato.copiaDe}. Vira um contato novo deste embarcador.</div>}
          {modalContato && modalContato.modo === "editar" && modalContato.contato.qtdEmbarcadoresMesmoEmail > 1 && (
            <div style={{ ...mutedStyle, marginBottom: "0.75rem", color: AUTOPILOT_COLORS.statusWarning }}>
              Vai atualizar este contato em {modalContato.contato.qtdEmbarcadoresMesmoEmail} embarcadores (mesmo e-mail).
            </div>
          )}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle}>Tipo *</Label>
                <Input type="select" value={formContato.idTipoContato} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, idTipoContato: valor })); }} disabled={modalContato && modalContato.modo === "editar"}>
                  <option value="">Selecione</option>
                  {tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle}>Nome *</Label>
                <Input value={formContato.nome} maxLength={60} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, nome: valor })); }} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label style={labelStyle}>E-mail</Label>
            <Input type="email" value={formContato.email} maxLength={100} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, email: valor })); }} />
          </FormGroup>
          <Row>
            <Col md="3"><FormGroup><Label style={labelStyle}>DDD</Label><Input value={formContato.dddTelefone} maxLength={4} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, dddTelefone: valor })); }} /></FormGroup></Col>
            <Col md="9"><FormGroup><Label style={labelStyle}>Telefone</Label><Input value={formContato.telefone} maxLength={20} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, telefone: valor })); }} /></FormGroup></Col>
          </Row>
          <Row>
            <Col md="3"><FormGroup><Label style={labelStyle}>DDD</Label><Input value={formContato.dddCelular} maxLength={4} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, dddCelular: valor })); }} /></FormGroup></Col>
            <Col md="9"><FormGroup><Label style={labelStyle}>Celular</Label><Input value={formContato.celular} maxLength={20} onChange={(e) => { const valor = e.target.value; setFormContato((f) => ({ ...f, celular: valor })); }} /></FormGroup></Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModalContato(null)} disabled={salvando}>Cancelar</Button>
          <Button color="info" onClick={salvarContato} disabled={salvando || !formContato.nome.trim() || (modalContato && modalContato.modo === "novo" && !formContato.idTipoContato)}>
            {salvando ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
